import { Icon } from "@mdi/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";

import { mdiCog, mdiPencil } from "@mdi/js";
import { Alert, Button, Input, Modal, Tooltip } from "antd";
import isEqual from "lodash/isEqual";

import useGeoMapLayers from "../hooks/getGeoMapLayers";
import useMutateGeoMapLayers from "../hooks/uploadRasterMap";
import useDeleteFieldTemplate from "../hooks/useDeleteFieldTemplate";
import useDeleteMapFromLayer from "../hooks/useDeleteMapFromLayer";
import useDeleteMapLayer from "../hooks/useDeleteMapLayer";
import useFetchFieldTemplates from "../hooks/useFetchFieldTemplates";
import useGetGeoMapSettings, {
  GetGeoMapSettingsKey
} from "../hooks/useGetGeoMapSettings";
import useMakeMap from "../hooks/useMakeMap";
import { usePostFieldTemplate } from "../hooks/usePostFieldTemplate";
import usePostMapLayer from "../hooks/usePostMapLayer";
import useUpdateGeoMap from "../hooks/useUpdateMap";
import useUpdateMapLayer from "../hooks/useUpdateMapLayer";
import { useUpdateSettings } from "../hooks/useUpdateSettings";
import styles from "./GeoMapLoaderModal.module.scss";
import {
  EditableLayer,
  GeoMapLoaderProps,
  Layer,
  PreservedUploadState
} from "./GeoMapLoaderModal.types";
import { LayerContainer } from "./ModalComponents/LayerContainer";
import { SettingsModal } from "./ModalComponents/SettingsModal";
import { TemplateNameModal } from "./ModalComponents/TemplateNameModal";
import { UnsavedChangesModal } from "./ModalComponents/UnsavedChangesModal";
import { useCreateModeHandlers } from "./hooks/useCreateModeHandlers";
import { useEditModeHandlers } from "./hooks/useEditModeHandlers";

// Auto-close delay after successful creation (in milliseconds)
const AUTO_CLOSE_DELAY_MS = 2500;

export function GeoMapLoader({
  open,
  onCancel,
  newMap,
  onUploadingChange,
  onUploadProgress,
  onReopen,
  editGroupName,
  editLayers
}: GeoMapLoaderProps) {
  const isEditMode = Boolean(editGroupName && editLayers && editLayers.length > 0);

  const [isEditingLayerGroup, setIsEditingLayerGroup] = useState(false);
  const [layerGroupName, setLayerGroupName] = useState("New Resource Play Group");
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [layers, setLayers] = useState<Layer[]>([
    {
      id: `layer-${Date.now()}`,
      name: "New Zone 1",
      maps: []
    }
  ]);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedCalculationMapTemplate, setSelectedCalculationMapTemplate] =
    useState("Single");
  const [showTemplateNameModal, setShowTemplateNameModal] = useState(false);
  const [editableLayers, setEditableLayers] = useState<EditableLayer[]>([]);
  const [newlyAddedLayerId, setNewlyAddedLayerId] = useState<string | null>(null);
  const [duplicateNameError, setDuplicateNameError] = useState<string | null>(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [backgroundUploadError, setBackgroundUploadError] = useState<string | null>(null);
  // State for recovering from failed uploads - preserves user's work if background upload fails
  const [preservedUploadState, setPreservedUploadState] =
    useState<PreservedUploadState | null>(null);

  const autoCloseTimerRef = useRef<NodeJS.Timeout | null>(null);
  const originalEditLayersRef = useRef<EditableLayer[]>([]);

  // Cleanup auto-close timer on unmount
  useEffect(() => {
    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
      }
    };
  }, []);

  // Handler for upload errors - preserves state for recovery and triggers reopen
  const handleUploadError = useCallback(
    (state: PreservedUploadState) => {
      setPreservedUploadState(state);
      // Trigger modal reopen if callback provided, passing edit mode info
      onReopen?.({
        isEditMode: state.isEditMode,
        groupName: state.layerGroupName
      });
    },
    [onReopen]
  );

  // Effect to restore preserved state when modal opens with preserved state
  useEffect(() => {
    if (open && preservedUploadState) {
      // Restore the preserved state
      setLayers(preservedUploadState.layers);
      setEditableLayers(preservedUploadState.editableLayers);
      setLayerGroupName(preservedUploadState.layerGroupName);
      if (preservedUploadState.selectedCalculationMapTemplate) {
        setSelectedCalculationMapTemplate(
          preservedUploadState.selectedCalculationMapTemplate
        );
      }
      // Set error message if present
      if (preservedUploadState.errorMessage) {
        setBackgroundUploadError(preservedUploadState.errorMessage);
      }
      // Clear the preserved state after restoring
      setPreservedUploadState(null);
    }
  }, [open, preservedUploadState]);

  const { data: settings } = useGetGeoMapSettings();
  const { data: calculationFieldTemplates } = useFetchFieldTemplates();
  const { mutateAsync: uploadMap } = useMutateGeoMapLayers();
  const { mutateAsync: addMapLayer } = usePostMapLayer();
  const { mutateAsync: makeMap } = useMakeMap();
  const { mutateAsync: updateMap } = useUpdateGeoMap();
  const { refetch: refetchGeoMapLayers } = useGeoMapLayers();
  const { mutateAsync: deleteFieldTemplate } = useDeleteFieldTemplate();
  const { mutateAsync: deleteMapFromLayer } = useDeleteMapFromLayer();
  const { mutateAsync: deleteMapLayer } = useDeleteMapLayer();
  const { mutateAsync: updateMapLayer } = useUpdateMapLayer();
  const { mutateAsync: saveSettings } = useUpdateSettings();
  const { mutateAsync: addNewFieldTemplateAsync } = usePostFieldTemplate();
  const queryClient = useQueryClient();

  // Initialize edit mode state when modal opens with edit layers
  // Only runs when modal opens (not on every editLayers change) to prevent resetting user edits
  // Skip if there's preserved state to restore (handled by the preserved state effect)
  useEffect(() => {
    if (preservedUploadState) {
      // Let the preserved state restore effect handle initialization
      return;
    }

    if (open && isEditMode && editLayers) {
      const initialEditableLayers = editLayers.map((layer) => ({
        ...layer,
        _selectedCalculationMapTemplate: "Single"
      }));
      setEditableLayers(initialEditableLayers);
      originalEditLayersRef.current = initialEditableLayers;
      if (editGroupName) {
        setLayerGroupName(editGroupName);
      }
      setLayers([]);
    }
  }, [open]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    // Check for new layers
    if (layers.some((layer) => layer.maps.length > 0)) {
      return true;
    }

    // In edit mode, check if group name or existing layers were modified
    if (isEditMode) {
      if (editGroupName && layerGroupName !== editGroupName) {
        return true;
      }
      return !isEqual(editableLayers, originalEditLayersRef.current);
    }

    return false;
  }, [layers, isEditMode, editableLayers, editGroupName, layerGroupName]);

  // handleClose needs to be defined before hooks since they depend on it
  const handleClose = useCallback(() => {
    // Refetch when closing edit mode to ensure cache is updated with any changes made
    if (isEditMode) {
      refetchGeoMapLayers();
    }

    // Reset all local state to initial values
    setIsEditingLayerGroup(false);
    setLayerGroupName("New Resource Play Group");
    setIsUploading(false);
    setIsSaving(false);
    setLayers([
      {
        id: `layer-${Date.now()}`,
        name: "New Zone 1",
        maps: []
      }
    ]);
    setSelectedCalculationMapTemplate("Single");
    setShowTemplateNameModal(false);
    setEditableLayers([]);
    setNewlyAddedLayerId(null);
    setDuplicateNameError(null);
    setBackgroundUploadError(null);
    setShowCloseConfirm(false);
    onCancel();
  }, [isEditMode, refetchGeoMapLayers, onCancel]);

  // Handle cancel with unsaved changes check
  const handleCancelClick = useCallback(() => {
    if (hasUnsavedChanges()) {
      setShowCloseConfirm(true);
    } else {
      handleClose();
    }
  }, [hasUnsavedChanges, handleClose]);

  // Create mode handlers
  const {
    handleFilesSelected,
    handleDeleteMap,
    handleMapChange,
    handleMapNameChanged,
    handleExpressionValueChanged,
    handleScaleValuesToggled,
    handleAddNewCalculationMap,
    handleSaveCalculationMapsAsNewTemplate,
    saveCalculationMapsAsNewTemplate,
    handleLayerNameChange,
    handleDeleteSelectedTemplate,
    handleAddLayer,
    handleDeleteNewLayer,
    handleOk,
    createCalculationMaps,
    processLayerMaps
  } = useCreateModeHandlers({
    layers,
    setLayers,
    layerGroupName,
    selectedCalculationMapTemplate,
    setSelectedCalculationMapTemplate,
    settings,
    calculationFieldTemplates,
    setIsUploading,
    setIsSaving,
    setDuplicateNameError,
    setShowTemplateNameModal,
    newlyAddedLayerId,
    setNewlyAddedLayerId,
    isEditMode,
    editableLayersCount: editableLayers.length,
    autoCloseTimerRef,
    autoCloseDelayMs: AUTO_CLOSE_DELAY_MS,
    handleClose,
    onUploadingChange,
    onUploadProgress,
    onUploadError: handleUploadError,
    setBackgroundUploadError,
    addMapLayer,
    uploadMap,
    makeMap,
    refetchGeoMapLayers: async () => {
      await refetchGeoMapLayers();
    },
    addNewFieldTemplateAsync,
    deleteFieldTemplate,
    deleteMapLayer,
    queryClient
  });

  // Edit mode handlers
  const {
    handleEditLayerFilesSelected,
    handleEditLayerDeleteMap,
    handleEditLayerNameChange,
    handleEditLayerNameSave,
    handleEditLayerMapChange,
    handleEditLayerMapNameChanged,
    handleEditLayerExpressionValueChanged,
    handleEditLayerScaleValuesToggled,
    handleEditLayerAddCalculationMap,
    handleDeleteExistingLayer,
    handleUpdate
  } = useEditModeHandlers({
    editableLayers,
    setEditableLayers,
    layers,
    layerGroupName,
    autoCloseTimerRef,
    autoCloseDelayMs: AUTO_CLOSE_DELAY_MS,
    handleClose,
    setIsSaving,
    setIsUploading,
    onUploadingChange,
    onUploadProgress,
    onUploadError: handleUploadError,
    setBackgroundUploadError,
    settings,
    uploadMap,
    deleteMapFromLayer,
    updateMapLayer,
    updateMap,
    deleteMapLayer,
    addMapLayer,
    makeMap,
    refetchGeoMapLayers: async () => {
      await refetchGeoMapLayers();
    },
    createCalculationMaps,
    processLayerMaps
  });

  return (
    <Modal
      className={styles.geoMapLoaderModal}
      open={open}
      maskClosable={false}
      onCancel={handleCancelClick}
      title={newMap ? "Add Geo Map" : "Edit Geo Map"}
      onOk={isEditMode ? handleUpdate : handleOk}
      width={"70%"}
      footer={
        // Show footer button when there's data (create mode with maps OR edit mode with layers)
        layers.some((layer) => layer.maps.length > 0) || isEditMode ? (
          <Button
            className={styles.createLayerMapButton}
            type="primary"
            loading={isSaving}
            disabled={
              isSaving ||
              !layerGroupName.trim() ||
              layers.some((layer) => !layer.name.trim()) ||
              (isEditMode && editableLayers.some((layer) => !layer.name.trim()))
            }
            onClick={isEditMode ? handleUpdate : handleOk}>
            {isSaving
              ? "Uploading... This may take a while"
              : isEditMode
              ? "Update Layer Map"
              : "Create Layer Map"}
          </Button>
        ) : null
      }>
      <div className={styles.modalContentContainer}>
        {backgroundUploadError && (
          <Alert
            message={backgroundUploadError}
            type="error"
            showIcon
            closable
            onClose={() => setBackgroundUploadError(null)}
            className={styles.backgroundUploadError}
          />
        )}

        <div className={styles.titleContainer}>
          <div className={styles.groupNameContainer}>
            {isEditingLayerGroup ? (
              <Input
                placeholder="Rename Layer Group"
                size="large"
                autoFocus
                value={layerGroupName}
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  setLayerGroupName(e.target.value);
                  // Clear duplicate name error when user changes the name
                  if (duplicateNameError) {
                    setDuplicateNameError(null);
                  }
                }}
                onPressEnter={() => {
                  if (layerGroupName.trim()) {
                    setIsEditingLayerGroup(false);
                  }
                }}
                onBlur={() => {
                  if (layerGroupName.trim()) {
                    setIsEditingLayerGroup(false);
                  }
                }}
              />
            ) : (
              <>
                <span className={styles.layerGroupName}>{layerGroupName}</span>

                <Tooltip title="Edit Layer Group Name">
                  <Button
                    className={styles.iconButton}
                    type="text"
                    icon={<Icon path={mdiPencil} size={"24px"} />}
                    onClick={() => setIsEditingLayerGroup(!isEditingLayerGroup)}
                    data-testid="edit-layer-group-button"
                  />
                </Tooltip>
              </>
            )}

            {duplicateNameError && (
              <div className={styles.duplicateNameError}>{duplicateNameError}</div>
            )}
          </div>

          {/* Show settings button when there's data in either mode */}
          {(layers.some((layer) => layer.maps.length > 0) ||
            editableLayers.length > 0) && (
            <Button
              className={styles.settingsButton}
              type="default"
              icon={<Icon path={mdiCog} size={"18px"} />}
              onClick={() => setShowSettings(true)}
              aria-label="Manage Custom Fields">
              Manage Custom Fields
            </Button>
          )}
        </div>

        <div className={styles.layersScrollContainer}>
          {isEditMode ? (
            <>
              {/* Existing layers */}
              {editableLayers.map((layer) => {
                const layerId = layer._id;
                if (!layerId) return null;
                return (
                  <LayerContainer
                    key={layerId}
                    layerName={layer.name}
                    onLayerNameChange={(name) => handleEditLayerNameChange(layerId, name)}
                    onLayerNameBlur={() => handleEditLayerNameSave(layerId)}
                    onFilesSelected={(files) =>
                      handleEditLayerFilesSelected(layerId, files)
                    }
                    isUploadingFile={isUploading}
                    maps={layer.maps}
                    mapTypes={settings?.getAllSettings() ?? []}
                    onDeleteMap={(map) => handleEditLayerDeleteMap(layerId, map)}
                    onMapChange={(map, changes) =>
                      handleEditLayerMapChange(layerId, map, changes)
                    }
                    onMapNameChanged={(map, name) =>
                      handleEditLayerMapNameChanged(layerId, map, name)
                    }
                    onExpressionChanged={(map, expression) =>
                      handleEditLayerExpressionValueChanged(layerId, map, expression)
                    }
                    onUseExpressionChanged={(map, checked) =>
                      handleEditLayerScaleValuesToggled(layerId, map, checked)
                    }
                    calculationFieldTemplates={calculationFieldTemplates}
                    selectedCalculationMapTemplate={
                      layer._selectedCalculationMapTemplate || "Single"
                    }
                    onCalculationMapTemplateChange={(value) => {
                      setEditableLayers((prev) =>
                        prev.map((l) =>
                          l._id === layerId
                            ? { ...l, _selectedCalculationMapTemplate: value }
                            : l
                        )
                      );
                    }}
                    onAddNewCalculationMap={() =>
                      handleEditLayerAddCalculationMap(layerId)
                    }
                    onSaveMapAsNewTemplate={handleSaveCalculationMapsAsNewTemplate}
                    onDeleteSelectedTemplate={handleDeleteSelectedTemplate}
                    isCollapseable={true}
                    onDeleteLayer={() => handleDeleteExistingLayer(layerId)}
                  />
                );
              })}
              {/* New layers */}
              {layers.map((layer) => (
                <LayerContainer
                  key={layer.id}
                  layerName={layer.name}
                  onLayerNameChange={(name) => handleLayerNameChange(layer.id, name)}
                  onFilesSelected={(files) => handleFilesSelected(layer.id, files)}
                  isUploadingFile={isUploading}
                  maps={layer.maps}
                  mapTypes={settings?.getAllSettings() ?? []}
                  onDeleteMap={(map) => handleDeleteMap(layer.id, map)}
                  onMapChange={(map, changes) => handleMapChange(layer.id, map, changes)}
                  onMapNameChanged={(map, name) =>
                    handleMapNameChanged(layer.id, map, name)
                  }
                  onExpressionChanged={(map, expression) =>
                    handleExpressionValueChanged(layer.id, map, expression)
                  }
                  onUseExpressionChanged={(map, checked) =>
                    handleScaleValuesToggled(layer.id, map, checked)
                  }
                  calculationFieldTemplates={calculationFieldTemplates}
                  selectedCalculationMapTemplate={selectedCalculationMapTemplate}
                  onCalculationMapTemplateChange={setSelectedCalculationMapTemplate}
                  onAddNewCalculationMap={() => handleAddNewCalculationMap(layer.id)}
                  onSaveMapAsNewTemplate={handleSaveCalculationMapsAsNewTemplate}
                  onDeleteSelectedTemplate={handleDeleteSelectedTemplate}
                  isCollapseable={editableLayers.length + layers.length > 1}
                  initialExpanded={layer.id === newlyAddedLayerId}
                  onDeleteLayer={() => handleDeleteNewLayer(layer.id)}
                />
              ))}

              {/* Add Layer button in edit mode */}
              <Button
                className={styles.addLayerButton}
                type="default"
                block
                onClick={handleAddLayer}>
                + Add Layer
              </Button>
            </>
          ) : (
            // Create mode: Render LayerContainers for each layer
            <>
              {layers.map((layer) => (
                <LayerContainer
                  key={layer.id}
                  layerName={layer.name}
                  onLayerNameChange={(name) => handleLayerNameChange(layer.id, name)}
                  onFilesSelected={(files) => handleFilesSelected(layer.id, files)}
                  isUploadingFile={isUploading}
                  maps={layer.maps}
                  mapTypes={settings?.getAllSettings() ?? []}
                  onDeleteMap={(map) => handleDeleteMap(layer.id, map)}
                  onMapChange={(map, changes) => handleMapChange(layer.id, map, changes)}
                  onMapNameChanged={(map, name) =>
                    handleMapNameChanged(layer.id, map, name)
                  }
                  onExpressionChanged={(map, expression) =>
                    handleExpressionValueChanged(layer.id, map, expression)
                  }
                  onUseExpressionChanged={(map, checked) =>
                    handleScaleValuesToggled(layer.id, map, checked)
                  }
                  calculationFieldTemplates={calculationFieldTemplates}
                  selectedCalculationMapTemplate={selectedCalculationMapTemplate}
                  onCalculationMapTemplateChange={setSelectedCalculationMapTemplate}
                  onAddNewCalculationMap={() => handleAddNewCalculationMap(layer.id)}
                  onSaveMapAsNewTemplate={handleSaveCalculationMapsAsNewTemplate}
                  onDeleteSelectedTemplate={handleDeleteSelectedTemplate}
                  isCollapseable={layers.length > 1}
                  initialExpanded={layer.id === newlyAddedLayerId}
                  onDeleteLayer={() => handleDeleteNewLayer(layer.id)}
                />
              ))}

              {layers.some((layer) => layer.maps.length > 0) && (
                <Button
                  className={styles.addLayerButton}
                  type="default"
                  block
                  onClick={handleAddLayer}>
                  + Add Layer
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <SettingsModal
        showSettings={showSettings}
        containerRef={null}
        settings={settings}
        onCancel={() => setShowSettings(false)}
        onSave={async (settings) => {
          await saveSettings(settings);
          setShowSettings(false);
          await queryClient.invalidateQueries(GetGeoMapSettingsKey);
        }}
      />

      <TemplateNameModal
        containerRef={null}
        isVisible={showTemplateNameModal}
        onOk={saveCalculationMapsAsNewTemplate}
        onCancel={() => setShowTemplateNameModal(false)}
      />

      <UnsavedChangesModal
        isVisible={showCloseConfirm}
        onConfirm={() => {
          setShowCloseConfirm(false);
          handleClose();
        }}
        onCancel={() => setShowCloseConfirm(false)}
        containerRef={null}
      />
    </Modal>
  );
}
